import { FC, useEffect, useRef } from 'react';
import {
  ControlBar,
  Player,
  ClosedCaptionButton,
  BigPlayButton,
  PlaybackRateMenuButton,
  PlayerReference,
} from 'video-react';
import { useComponentTracking } from '../../hooks/useComponentTracking';
import { Youtube } from 'lucide-react';

interface Props {
  videoUrl?: string | null;
  captionUrlEnglish?: string | null;
  captionUrlSpanish?: string | null;
}

export const WorkedExample: FC<Props> = ({ videoUrl, captionUrlEnglish, captionUrlSpanish }) => {
  const playerRef = useRef<PlayerReference>(null);

  // root reference for applying a11y patches
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Track component lifecycle for analytics
  useComponentTracking('Worked Example');

  // A11y patch for video-react: avoid nested focusable controls and add missing aria-expanded
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const patchExpandableMenu = (opts: {
      toggleSelector: string;
      menuSelector: string;
      label: string;
      menuId: string;
    }) => {
      const toggle = root.querySelector<HTMLElement>(opts.toggleSelector);
      if (!toggle) return;

      toggle.setAttribute('aria-label', opts.label);
      toggle.setAttribute('aria-haspopup', 'menu');
      const menu = root.querySelector<HTMLElement>(opts.menuSelector);
      const expanded = toggle.classList.contains('video-react-menu-button-active') || !!menu;
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');

      if (menu) {
        menu.id = opts.menuId;
        toggle.setAttribute('aria-controls', opts.menuId);
      } else {
        toggle.removeAttribute('aria-controls');
      }
    };

    const patchVolumeNesting = () => {
      const wrapper = root.querySelector<HTMLElement>(
        '.video-react-volume-menu-button-horizontal[role="button"], .video-react-volume-menu-button-horizontal[tabindex="0"]',
      );
      if (!wrapper) return;

      const slider = wrapper.querySelector<HTMLElement>('[role="slider"]');
      if (!slider) return;

      // keep only the slider as interactive to avoid nested interactive controls
      wrapper.removeAttribute('role');
      wrapper.removeAttribute('tabindex');

      if (!slider.getAttribute('aria-label')) slider.setAttribute('aria-label', 'Volume');
    };

    const run = () => {
      patchExpandableMenu({
        toggleSelector: '.video-react-playback-rate .video-react-menu-button',
        menuSelector: '.video-react-playback-rate .video-react-menu',
        label: 'Playback rate',
        menuId: 'playback-rate-menu',
      });

      patchExpandableMenu({
        toggleSelector: '.video-react-closed-caption .video-react-menu-button',
        menuSelector: '.video-react-closed-caption .video-react-menu',
        label: 'Closed captions',
        menuId: 'closed-captions-menu',
      });

      patchVolumeNesting();
    };

    run();

    // Keep patches stable if video-react re-injects attrs during re-renders
    const obs = new MutationObserver(run);
    obs.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'role', 'tabindex'],
    });

    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={rootRef}
      className="w-full flex flex-column justify-content-start align-items-start nc_block"
      id="workedExample"
    >
      <h1 className="tw-flex tw-items-center tw-w-100 tw-p-1">
        <Youtube className="tw-mr-4" /> Worked Example Video
      </h1>
      <h3 role="presentation">Watch this video to see how you might work through these types of problems</h3>

      <Player
        ref={playerRef}
        onPlay={() => {}}
        onEnded={() => {}}
        onLoadStart={() => {}}
        onPause={() => {}}
        videoId=""
        crossOrigin="anonymous"
      >
        <BigPlayButton position="center" />
        <source src={videoUrl || ''} />
        <track kind="captions" src={captionUrlEnglish || ''} srcLang="en" label="English" default />
        <track kind="captions" src={captionUrlSpanish || ''} srcLang="es" label="Spanish" default />
        <ControlBar>
          <PlaybackRateMenuButton rates={[1.5, 1.25, 1, 0.75, 0.5]} />
          <ClosedCaptionButton order={7} />
        </ControlBar>
      </Player>
    </div>
  );
};
